// ─── Plan Handle Constants ──────────────────────────────────────────
export const PLAN_HANDLES = {
  FREE: "free",
  STARTER: "starter",
  GROWTH: "growth",
  SCALE: "scale",
} as const;

export type PlanHandle = (typeof PLAN_HANDLES)[keyof typeof PLAN_HANDLES];

export const BILLING_INTERVALS = {
  MONTHLY: "monthly",
  YEARLY: "yearly",
} as const;

export type PlanInterval =
  (typeof BILLING_INTERVALS)[keyof typeof BILLING_INTERVALS];

export const BILLING_UI_INTERVALS = {
  MONTHLY: "monthly",
  ANNUAL: "annual",
} as const;

export type BillingUiInterval =
  (typeof BILLING_UI_INTERVALS)[keyof typeof BILLING_UI_INTERVALS];

export const BILLING_INTERVAL_LABELS = {
  MONTHLY_SHORT: "mo",
  YEARLY_SHORT: "yr",
} as const;

export function toDataInterval(ui: BillingUiInterval): PlanInterval {
  return ui === BILLING_UI_INTERVALS.ANNUAL
    ? BILLING_INTERVALS.YEARLY
    : BILLING_INTERVALS.MONTHLY;
}

export function toUiInterval(data: PlanInterval): BillingUiInterval {
  return data === BILLING_INTERVALS.YEARLY
    ? BILLING_UI_INTERVALS.ANNUAL
    : BILLING_UI_INTERVALS.MONTHLY;
}

export const BILLING_PROVIDERS = {
  SHOPIFY_APP_PRICING: "shopify_app_pricing",
  PADDLE: "paddle",
  STRIPE: "stripe",
  ZOHO: "zoho",
} as const;

export type BillingProvider =
  (typeof BILLING_PROVIDERS)[keyof typeof BILLING_PROVIDERS];

export const SHOPIFY_BILLING_PERIODS = {
  ANNUAL: "ANNUAL",
  EVERY_30_DAYS: "EVERY_30_DAYS",
} as const;

// ─── Gated Feature Constants ────────────────────────────────────────
export const GATED_FEATURES = {
  TEAM: "team",
  VIP: "vip",
} as const;

export type GatedFeature = (typeof GATED_FEATURES)[keyof typeof GATED_FEATURES];

// ─── Feature Key Constants ──────────────────────────────────────────
export const FEATURE_KEYS = {
  TEAM_MEMBERS: "team_members",
  ORDER_MANAGEMENT: "order_management",
  BASIC_ANALYTICS: "basic_analytics",
  PRIORITY_SUPPORT: "priority_support",
  TEAM: "team",
  VIP_CUSTOMERS: "vip_customers",
  ACCOUNT_MANAGER: "account_manager",
} as const;

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS];

// ─── Plan Tier Order ────────────────────────────────────────────────
export const PLAN_TIER_ORDER: readonly PlanHandle[] = [
  PLAN_HANDLES.FREE,
  PLAN_HANDLES.STARTER,
  PLAN_HANDLES.GROWTH,
  PLAN_HANDLES.SCALE,
];

// ─── Plan Definitions ───────────────────────────────────────────────
export function calculateAnnualPrice(monthlyPrice: number): number {
  if (monthlyPrice === 0) return 0;
  return Math.round(monthlyPrice * 9.6 * 10) / 10;
}

export const BILLING_PLANS = {
  [PLAN_HANDLES.FREE]: {
    handle: PLAN_HANDLES.FREE,
    name: "Free",
    prices: { monthly: 0, yearly: 0 },
    currency: "USD",
    activeUserLimit: 1,
    isFree: true,
  },
  [PLAN_HANDLES.STARTER]: {
    handle: PLAN_HANDLES.STARTER,
    name: "Starter",
    prices: { monthly: 19, yearly: calculateAnnualPrice(19) },
    currency: "USD",
    activeUserLimit: 3,
    isFree: false,
  },
  [PLAN_HANDLES.GROWTH]: {
    handle: PLAN_HANDLES.GROWTH,
    name: "Growth",
    prices: { monthly: 39, yearly: calculateAnnualPrice(39) },
    currency: "USD",
    activeUserLimit: 7,
    isFree: false,
  },
  [PLAN_HANDLES.SCALE]: {
    handle: PLAN_HANDLES.SCALE,
    name: "Scale",
    prices: { monthly: 79, yearly: calculateAnnualPrice(79) },
    currency: "USD",
    activeUserLimit: 15,
    isFree: false,
  },
} as const;

// ─── Tier UI Config (shared metadata) ───────────────────────────────
export const PLAN_TIERS = {
  [PLAN_HANDLES.FREE]: {
    label: "For solo entrepreneurs",
    iconKey: "feather",
    ui: {
      iconBg: "bg-slate-100 dark:bg-slate-800/60",
      iconText: "text-slate-500 dark:text-slate-400",
      gradient: "linear-gradient(90deg, #94a3b8, #cbd5e1)",
      glow: "hsl(var(--muted-foreground) / 0.12)",
      glowRgba: "rgba(148,163,184,0.15)",
      ring: "",
    },
  },
  [PLAN_HANDLES.STARTER]: {
    label: "For small teams",
    iconKey: "rocket",
    ui: {
      iconBg: "bg-blue-50 dark:bg-blue-950/50",
      iconText: "text-blue-600 dark:text-blue-400",
      gradient: "linear-gradient(90deg, #3b82f6, #60a5fa)",
      glow: "rgba(59,130,246,0.15)",
      glowRgba: "rgba(59,130,246,0.15)",
      ring: "",
    },
  },
  [PLAN_HANDLES.GROWTH]: {
    label: "For growing businesses",
    iconKey: "trending-up",
    ui: {
      iconBg: "bg-indigo-50 dark:bg-indigo-950/50",
      iconText: "text-indigo-600 dark:text-indigo-400",
      gradient: "linear-gradient(90deg, #6366f1, #a78bfa)",
      glow: "rgba(99,102,241,0.15)",
      glowRgba: "rgba(99,102,241,0.15)",
      ring: "ring-2 ring-indigo-500/20",
    },
  },
  [PLAN_HANDLES.SCALE]: {
    label: "For scaling operations",
    iconKey: "crown",
    ui: {
      iconBg: "bg-amber-50 dark:bg-amber-950/50",
      iconText: "text-amber-600 dark:text-amber-400",
      gradient: "linear-gradient(90deg, #f59e0b, #fbbf24)",
      glow: "rgba(245,158,11,0.15)",
      glowRgba: "rgba(245,158,11,0.15)",
      ring: "",
    },
  },
} as const;

// ─── Feature Registry (unified source of truth) ─────────────────────
export const PLAN_FEATURES_REGISTRY = {
  [FEATURE_KEYS.TEAM_MEMBERS]: {
    key: FEATURE_KEYS.TEAM_MEMBERS,
    label: "Users",
    iconKey: "users",
    values: {
      [PLAN_HANDLES.FREE]: "1",
      [PLAN_HANDLES.STARTER]: "3",
      [PLAN_HANDLES.GROWTH]: "7",
      [PLAN_HANDLES.SCALE]: "15",
    },
    isGated: false,
    gatedFeature: null,
  },
  [FEATURE_KEYS.ORDER_MANAGEMENT]: {
    key: FEATURE_KEYS.ORDER_MANAGEMENT,
    label: "Order Management",
    iconKey: "zap",
    values: {
      [PLAN_HANDLES.FREE]: true,
      [PLAN_HANDLES.STARTER]: true,
      [PLAN_HANDLES.GROWTH]: true,
      [PLAN_HANDLES.SCALE]: true,
    },
    isGated: false,
    gatedFeature: null,
  },
  [FEATURE_KEYS.BASIC_ANALYTICS]: {
    key: FEATURE_KEYS.BASIC_ANALYTICS,
    label: "Basic Analytics",
    iconKey: "bar-chart",
    values: {
      [PLAN_HANDLES.FREE]: true,
      [PLAN_HANDLES.STARTER]: true,
      [PLAN_HANDLES.GROWTH]: true,
      [PLAN_HANDLES.SCALE]: true,
    },
    isGated: false,
    gatedFeature: null,
  },
  [FEATURE_KEYS.PRIORITY_SUPPORT]: {
    key: FEATURE_KEYS.PRIORITY_SUPPORT,
    label: "Priority Support",
    iconKey: "check",
    values: {
      [PLAN_HANDLES.FREE]: false,
      [PLAN_HANDLES.STARTER]: true,
      [PLAN_HANDLES.GROWTH]: true,
      [PLAN_HANDLES.SCALE]: true,
    },
    isGated: false,
    gatedFeature: null,
  },
  [FEATURE_KEYS.TEAM]: {
    key: FEATURE_KEYS.TEAM,
    label: "Team Management",
    iconKey: "users",
    values: {
      [PLAN_HANDLES.FREE]: false,
      [PLAN_HANDLES.STARTER]: false,
      [PLAN_HANDLES.GROWTH]: true,
      [PLAN_HANDLES.SCALE]: true,
    },
    isGated: true,
    gatedFeature: GATED_FEATURES.TEAM,
  },
  [FEATURE_KEYS.VIP_CUSTOMERS]: {
    key: FEATURE_KEYS.VIP_CUSTOMERS,
    label: "VIP Customers",
    iconKey: "crown",
    values: {
      [PLAN_HANDLES.FREE]: false,
      [PLAN_HANDLES.STARTER]: false,
      [PLAN_HANDLES.GROWTH]: true,
      [PLAN_HANDLES.SCALE]: true,
    },
    isGated: true,
    gatedFeature: GATED_FEATURES.VIP,
  },
  [FEATURE_KEYS.ACCOUNT_MANAGER]: {
    key: FEATURE_KEYS.ACCOUNT_MANAGER,
    label: "Dedicated Account Manager",
    iconKey: "crown",
    values: {
      [PLAN_HANDLES.FREE]: false,
      [PLAN_HANDLES.STARTER]: false,
      [PLAN_HANDLES.GROWTH]: false,
      [PLAN_HANDLES.SCALE]: true,
    },
    isGated: false,
    gatedFeature: null,
  },
} as const;

export type FeatureRegistryEntry = (typeof PLAN_FEATURES_REGISTRY)[FeatureKey];

// ─── Tier Feature Deltas (new features per tier) ────────────────────
export const PLAN_TIER_FEATURES: Record<PlanHandle, readonly FeatureKey[]> = {
  [PLAN_HANDLES.FREE]: [
    FEATURE_KEYS.TEAM_MEMBERS,
    FEATURE_KEYS.ORDER_MANAGEMENT,
    FEATURE_KEYS.BASIC_ANALYTICS,
  ],
  [PLAN_HANDLES.STARTER]: [
    FEATURE_KEYS.TEAM_MEMBERS,
    FEATURE_KEYS.PRIORITY_SUPPORT,
  ],
  [PLAN_HANDLES.GROWTH]: [
    FEATURE_KEYS.TEAM_MEMBERS,
    FEATURE_KEYS.TEAM,
    FEATURE_KEYS.VIP_CUSTOMERS,
  ],
  [PLAN_HANDLES.SCALE]: [
    FEATURE_KEYS.TEAM_MEMBERS,
    FEATURE_KEYS.ACCOUNT_MANAGER,
  ],
};

export function getCumulativeFeatures(handle: PlanHandle): FeatureKey[] {
  const normalized = normalizePlanHandle(handle) as PlanHandle;
  const idx = PLAN_TIER_ORDER.indexOf(normalized);
  if (idx < 0) return [...PLAN_TIER_FEATURES[PLAN_HANDLES.FREE]];
  const seen = new Set<FeatureKey>();
  return PLAN_TIER_ORDER.slice(0, idx + 1).flatMap((h) =>
    PLAN_TIER_FEATURES[h].filter((key) => {
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
  );
}

// ─── Gated Feature → Plan Mapping (derived from registry) ───────────
export const PLAN_GATED_FEATURES: Record<GatedFeature, readonly PlanHandle[]> =
  {
    [GATED_FEATURES.TEAM]: [PLAN_HANDLES.GROWTH, PLAN_HANDLES.SCALE],
    [GATED_FEATURES.VIP]: [PLAN_HANDLES.GROWTH, PLAN_HANDLES.SCALE],
  };

export const FEATURE_GATE_LABELS: Record<GatedFeature, string> = {
  [GATED_FEATURES.TEAM]: "Team Management",
  [GATED_FEATURES.VIP]: "VIP Customers",
};

// ─── Backward-compatible display features (derived from registry) ───
export const PLAN_DISPLAY_FEATURES = Object.values(PLAN_FEATURES_REGISTRY).map(
  (entry) => ({
    key: entry.key,
    label: entry.label,
    plans: entry.values,
  }),
);

// ─── Legacy PLAN_FEATURES (derived) ─────────────────────────────────
export const PLAN_FEATURES: Record<
  PlanHandle,
  { userLimit: number; label: string }
> = {
  [PLAN_HANDLES.FREE]: {
    userLimit: 1,
    label: PLAN_TIERS[PLAN_HANDLES.FREE].label,
  },
  [PLAN_HANDLES.STARTER]: {
    userLimit: 3,
    label: PLAN_TIERS[PLAN_HANDLES.STARTER].label,
  },
  [PLAN_HANDLES.GROWTH]: {
    userLimit: 7,
    label: PLAN_TIERS[PLAN_HANDLES.GROWTH].label,
  },
  [PLAN_HANDLES.SCALE]: {
    userLimit: 15,
    label: PLAN_TIERS[PLAN_HANDLES.SCALE].label,
  },
};

// ─── Invoice Pagination Constants ───────────────────────────────────
export const INVOICE_DEFAULT_PAGE_SIZE = 10;
export const INVOICE_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
export type InvoicePageSize = (typeof INVOICE_PAGE_SIZE_OPTIONS)[number];

export const BILLING_SYNC_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
} as const;

export type BillingSyncStatus =
  (typeof BILLING_SYNC_STATUS)[keyof typeof BILLING_SYNC_STATUS];

export const BILLING_SYNC_RESET_DELAY_MS = 3000;

export const BILLING_SYNC_MESSAGES = {
  SYNCED: "Subscription synced successfully",
  FAILED: "Failed to sync subscription",
} as const;

// ─── Utility Functions ──────────────────────────────────────────────
export function normalizePlanHandle(handle: string): string {
  return handle.replace(/-test$/, "");
}

export function isTestPlanHandle(handle: string): boolean {
  return handle.endsWith("-test");
}

export function getPlanByHandle(handle: string) {
  const normalized = normalizePlanHandle(handle) as PlanHandle;
  return BILLING_PLANS[normalized] ?? BILLING_PLANS[PLAN_HANDLES.FREE];
}

export function resolvePlanHandleFromName(
  displayName: string,
): PlanHandle | null {
  const normalized = normalizePlanHandle(displayName).toLowerCase();
  for (const handle of PLAN_TIER_ORDER) {
    const planName = BILLING_PLANS[handle].name.toLowerCase();
    if (normalized === planName || normalized === `${planName} plan`) {
      return handle;
    }
  }
  return null;
}

export function getNextPlan(currentHandle: string): PlanHandle | null {
  const normalized = normalizePlanHandle(currentHandle) as PlanHandle;
  const idx = PLAN_TIER_ORDER.indexOf(normalized);
  if (idx < 0 || idx >= PLAN_TIER_ORDER.length - 1) return null;
  return PLAN_TIER_ORDER[idx + 1]!;
}

export function getActiveUserLimit(handle: string): number {
  return getPlanByHandle(handle).activeUserLimit;
}

export function isFreePlan(handle: string): boolean {
  return getPlanByHandle(handle).isFree;
}

export function isGrowthOrAbove(planHandle: string): boolean {
  const normalized = normalizePlanHandle(planHandle) as PlanHandle;
  const idx = PLAN_TIER_ORDER.indexOf(normalized);
  return idx >= PLAN_TIER_ORDER.indexOf(PLAN_HANDLES.GROWTH);
}

export function isFeatureEnabledForPlan(
  feature: GatedFeature,
  planHandle: string,
): boolean {
  const normalized = normalizePlanHandle(planHandle) as PlanHandle;
  return PLAN_GATED_FEATURES[feature]?.includes(normalized) ?? false;
}

export function getAnnualMonthlyEquiv(planHandle: PlanHandle): number {
  const plan = BILLING_PLANS[planHandle];
  if (plan.prices.monthly === 0) return 0;
  return Math.round(plan.prices.monthly * 0.8 * 10) / 10;
}

export function formatPlanPrice(price: number): string {
  if (price === 0) return "0";
  if (Number.isInteger(price)) return price.toString();
  return price.toFixed(1);
}

function addMonthsToPeriod(start: Date, months: number): Date {
  const result = new Date(start);
  result.setMonth(result.getMonth() + months);
  if (result.getDate() !== start.getDate()) {
    result.setDate(0);
  }
  return result;
}

export function computePeriodEnd(start: Date, interval: PlanInterval): Date {
  if (interval === BILLING_INTERVALS.YEARLY) {
    const result = new Date(start);
    result.setFullYear(result.getFullYear() + 1);
    if (result.getDate() !== start.getDate()) {
      result.setDate(0);
    }
    return result;
  }
  return addMonthsToPeriod(start, 1);
}
